import * as signalR from "@microsoft/signalr";
import queueApi from "./api";

const CACHE_KEY = 'queue_cache_data';
const CACHE_TIMESTAMP_KEY = 'queue_cache_timestamp';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 соат дар миллисекунд

const STABLE_FIELDS_CACHE_VERSION = 1;

class QueueSignalRService {
  connection = null;
  queueViewListeners = [];
  connectionStateListeners = [];
  terminalId = null;
  currentQueueState = { called: [], waiting: [], countWaiting: 0, countCalled: 0 };

  createEmptyStableCache() {
    return { version: STABLE_FIELDS_CACHE_VERSION, tickets: {} };
  }

  mergeStableFieldsIntoTicket(ticket, stableEntry) {
    if (!ticket) return ticket;
    if (!stableEntry) {
      return {
        ...ticket,
        shelfId: (ticket.shelfCodes && ticket.shelfCodes.trim() !== "") ? ticket.shelfCodes : (ticket.shelfId ?? "")
      };
    }

    // API-first logic: prioritize fresh API data, fallback to cache only if API has no meaningful data
    const apiHasShelfCodes = ticket.shelfCodes && ticket.shelfCodes.trim() !== "";
    const apiHasClientTypeId = ticket.clientTypeId && ticket.clientTypeId > 0;
    const apiHasClientTypeName = ticket.clientTypeName && ticket.clientTypeName.trim() !== "";
    const apiHasClientPhoneNumber = ticket.clientPhoneNumber && ticket.clientPhoneNumber.trim() !== "";

    const mergedTicket = {
      ...ticket,
      clientTypeId: apiHasClientTypeId ? ticket.clientTypeId : (stableEntry.clientTypeId && stableEntry.clientTypeId > 0 ? stableEntry.clientTypeId : 0),
      clientTypeName: apiHasClientTypeName ? ticket.clientTypeName : (stableEntry.clientTypeName && stableEntry.clientTypeName.trim() !== "" ? stableEntry.clientTypeName : ""),
      clientPhoneNumber: apiHasClientPhoneNumber ? ticket.clientPhoneNumber : (stableEntry.clientPhoneNumber && stableEntry.clientPhoneNumber.trim() !== "" ? stableEntry.clientPhoneNumber : ""),
      shelfCodes: apiHasShelfCodes ? ticket.shelfCodes : (stableEntry.shelfCodes && stableEntry.shelfCodes.trim() !== "" ? stableEntry.shelfCodes : ""),
      shelfId: apiHasShelfCodes ? ticket.shelfCodes : (stableEntry.shelfCodes && stableEntry.shelfCodes.trim() !== "" ? stableEntry.shelfCodes : (ticket.shelfId ?? "")),
      isPhoneNumber: ticket.isPhoneNumber ?? stableEntry.isPhoneNumber ?? false
    };
    
    // Ensure isFromPhoneNumber is preserved from cache
    if (mergedTicket.terminalService?.service?.isFromPhoneNumber === undefined && stableEntry.isFromPhoneNumber !== undefined) {
      mergedTicket.terminalService = {
        ...mergedTicket.terminalService,
        service: {
          ...mergedTicket.terminalService?.service,
          isFromPhoneNumber: stableEntry.isFromPhoneNumber
        }
      };
    }
    
    return mergedTicket;
  }

  getStableCache() {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (!cachedData || !cachedTimestamp) {
        return this.createEmptyStableCache();
      }

      const timestamp = parseInt(cachedTimestamp, 10);
      const now = Date.now();

      if (now - timestamp > CACHE_TTL) {
        this.clearCache();
        return this.createEmptyStableCache();
      }

      const data = JSON.parse(cachedData);
      if (!data || typeof data !== 'object') return this.createEmptyStableCache();
      if (!data.tickets || typeof data.tickets !== 'object') return this.createEmptyStableCache();

      const result = {
        version: data.version || STABLE_FIELDS_CACHE_VERSION,
        tickets: data.tickets
      };
      
      return result;
    } catch (error) {
      console.error("❌ [Cache] Хатои хондани кеш:", error);
      return this.createEmptyStableCache();
    }
  }

  saveStableCache(stableCache) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(stableCache));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error("❌ [Cache] Хатои захираи кеш:", error);
    }
  }

  upsertStableFields(ticket) {
    if (!ticket || ticket.id === undefined || ticket.id === null) return;
    const stableCache = this.getStableCache();
    const existingEntry = stableCache.tickets[ticket.id];

    // API-first: update cache with API data when valid, otherwise preserve existing meaningful values
    const apiHasValidClientTypeId = ticket.clientTypeId && ticket.clientTypeId > 0;
    const apiHasValidShelfCodes = ticket.shelfCodes && ticket.shelfCodes.trim() !== "";
    const apiHasValidClientPhoneNumber = ticket.clientPhoneNumber && ticket.clientPhoneNumber.trim() !== "";
    const apiHasValidClientTypeName = ticket.clientTypeName && ticket.clientTypeName.trim() !== "";

    const cacheEntry = {
      id: ticket.id,
      // API-first: use API data when valid, fallback to cache only if API has no meaningful data
      clientTypeId: apiHasValidClientTypeId ? ticket.clientTypeId : (existingEntry?.clientTypeId && existingEntry.clientTypeId > 0 ? existingEntry.clientTypeId : 0),
      clientTypeName: apiHasValidClientTypeName ? ticket.clientTypeName : (existingEntry?.clientTypeName && existingEntry.clientTypeName.trim() !== "" ? existingEntry.clientTypeName : ""),
      shelfCodes: apiHasValidShelfCodes ? ticket.shelfCodes : (existingEntry?.shelfCodes && existingEntry.shelfCodes.trim() !== "" ? existingEntry.shelfCodes : ""),
      clientPhoneNumber: apiHasValidClientPhoneNumber ? ticket.clientPhoneNumber : (existingEntry?.clientPhoneNumber && existingEntry.clientPhoneNumber.trim() !== "" ? existingEntry.clientPhoneNumber : ""),
      isFromPhoneNumber: ticket.terminalService?.service?.isFromPhoneNumber ?? existingEntry?.isFromPhoneNumber ?? false,
      isPhoneNumber: ticket.isPhoneNumber ?? existingEntry?.isPhoneNumber ?? false
    };
    
    stableCache.tickets[ticket.id] = cacheEntry;
    this.saveStableCache(stableCache);
  }

  async connect(terminalId) {
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      return true;
    }

    const token = queueApi.getToken();
    if (!token) {
      console.error("❌ [SignalR] Токен мавҷуд нест");
      return false;
    }

    this.terminalId = terminalId;

    try {
      this.connection = new signalR.HubConnectionBuilder()
          .withUrl(`${queueApi.baseURL}/hubs/terminal?access_token=${encodeURIComponent(token)}`, {
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .configureLogging(signalR.LogLevel.Information)
          .build();

      this.connection.on("TicketUpdated", (ticket) => {
        console.log("✅ [SignalR] TicketUpdated received:", ticket.id, ticket.status, "— updating single ticket locally");
        this.handleTicketUpdate(ticket);
      });
      
      // Гӯш кардани ба ҳамаи event-ҳои SignalR
      this.connection.onreconnecting(() => {
        console.log("🔄 [SignalR] Reconnecting...");
        this.notifyConnectionStateListeners("reconnecting");
      });

      this.connection.onreconnected(async () => {
        console.log("✅ [SignalR] Reconnected");
        this.notifyConnectionStateListeners("connected");
        await this.refreshQueue();
      });

      this.connection.onclose(() => {
        console.error("❌ [SignalR] Connection closed");
        this.notifyConnectionStateListeners("disconnected");
      });
      
      
      await this.connection.start();

      this.notifyConnectionStateListeners("connected");
      return true;
    } catch (err) {
      console.error("❌ [SignalR] Хатои пайваст:", err);
      this.notifyConnectionStateListeners("disconnected");
      return false;
    }
  }

  // Кор бо тикети ягона
  handleTicketUpdate(ticket) {
    if (!ticket) return;
    
    this.upsertStableFields(ticket);

    const { called, waiting } = this.currentQueueState;
    const status = ticket.status;
    const isWaiting = status === 1; // QueueStatus.Waiting
    const isInProgress = status === 2; // QueueStatus.InProgress
    const isCompleted = status === 3 || status === 4 || !ticket.isActive; // Completed/Canceled

    const existingTicket = [...called, ...waiting].find(t => t.id === ticket.id);

    let newCalled = [...called];
    let newWaiting = [...waiting];

    // Тоза кардани тикет аз ҳарду массив
    newCalled = newCalled.filter(t => t.id !== ticket.id);
    newWaiting = newWaiting.filter(t => t.id !== ticket.id);

    // Илова кардан ба массиви дуруст
    if (isInProgress && !isCompleted) {
      const stableCache = this.getStableCache();
      const cachedTicket = stableCache.tickets[ticket.id];
      
      // API-first logic: prioritize fresh API data over cached data
      const apiHasShelfCodes = ticket.shelfCodes && ticket.shelfCodes.trim() !== "";
      const apiHasClientTypeId = ticket.clientTypeId && ticket.clientTypeId > 0;
      const apiHasClientTypeName = ticket.clientTypeName && ticket.clientTypeName.trim() !== "";
      const apiHasClientPhoneNumber = ticket.clientPhoneNumber && ticket.clientPhoneNumber.trim() !== "";
      
      const ticketItem = {
        ...ticket,
        ...(existingTicket || {}),
        id: ticket.id,
        code: ticket.code ?? existingTicket?.code,
        // API-first: use API data when valid, fallback to cache only if API has no meaningful data
        clientTypeId: apiHasClientTypeId ? ticket.clientTypeId : (cachedTicket?.clientTypeId && cachedTicket.clientTypeId > 0 ? cachedTicket.clientTypeId : (existingTicket?.clientTypeId ?? 0)),
        clientTypeName: apiHasClientTypeName ? ticket.clientTypeName : (cachedTicket?.clientTypeName && cachedTicket.clientTypeName.trim() !== "" ? cachedTicket.clientTypeName : (existingTicket?.clientTypeName ?? "")),
        clientPhoneNumber: apiHasClientPhoneNumber ? ticket.clientPhoneNumber : (cachedTicket?.clientPhoneNumber && cachedTicket.clientPhoneNumber.trim() !== "" ? cachedTicket.clientPhoneNumber : (existingTicket?.clientPhoneNumber ?? "")),
        shelfCodes: apiHasShelfCodes ? ticket.shelfCodes : (cachedTicket?.shelfCodes && cachedTicket.shelfCodes.trim() !== "" ? cachedTicket.shelfCodes : (existingTicket?.shelfCodes ?? "")),
        shelfId: apiHasShelfCodes ? ticket.shelfCodes : (cachedTicket?.shelfCodes && cachedTicket.shelfCodes.trim() !== "" ? cachedTicket.shelfCodes : (existingTicket?.shelfCodes ?? existingTicket?.shelfId ?? "")),
        isPhoneNumber: ticket.isPhoneNumber ?? cachedTicket?.isPhoneNumber ?? existingTicket?.isPhoneNumber ?? false,
        terminalServiceId: ticket.terminalServiceId ?? existingTicket?.terminalServiceId,
        terminalService: ticket.terminalService ?? existingTicket?.terminalService,
        status: this.getStatusString(ticket.status),
        windowNumber: ticket.cashier?.numberCashier || 0,
        dateCreated: ticket.dateCreated ?? existingTicket?.dateCreated,
        update: new Date().toISOString()
      };
      
      if (ticketItem.terminalService?.service?.isFromPhoneNumber === undefined && cachedTicket?.isFromPhoneNumber !== undefined) {
        ticketItem.terminalService = {
          ...ticketItem.terminalService,
          service: {
            ...ticketItem.terminalService?.service,
            isFromPhoneNumber: cachedTicket.isFromPhoneNumber
          }
        };
      }
      newCalled = [ticketItem, ...newCalled];
      
    } else if (isWaiting && !isCompleted) {
      // Гирифтани маълумоти кеш барои shelfCodes
      const stableCache = this.getStableCache();
      const cachedTicket = stableCache.tickets[ticket.id];
      
      // API-first logic: prioritize fresh API data over cached data
      const apiHasShelfCodes = ticket.shelfCodes && ticket.shelfCodes.trim() !== "";
      const apiHasClientTypeId = ticket.clientTypeId && ticket.clientTypeId > 0;
      const apiHasClientTypeName = ticket.clientTypeName && ticket.clientTypeName.trim() !== "";
      const apiHasClientPhoneNumber = ticket.clientPhoneNumber && ticket.clientPhoneNumber.trim() !== "";
      
      const ticketItem = {
        ...ticket,
        ...(existingTicket || {}),
        id: ticket.id,
        code: ticket.code ?? existingTicket?.code,
        // API-first: use API data when valid, fallback to cache only if API has no meaningful data
        clientTypeId: apiHasClientTypeId ? ticket.clientTypeId : (cachedTicket?.clientTypeId && cachedTicket.clientTypeId > 0 ? cachedTicket.clientTypeId : (existingTicket?.clientTypeId ?? 0)),
        clientTypeName: apiHasClientTypeName ? ticket.clientTypeName : (cachedTicket?.clientTypeName && cachedTicket.clientTypeName.trim() !== "" ? cachedTicket.clientTypeName : (existingTicket?.clientTypeName ?? "")),
        clientPhoneNumber: apiHasClientPhoneNumber ? ticket.clientPhoneNumber : (cachedTicket?.clientPhoneNumber && cachedTicket.clientPhoneNumber.trim() !== "" ? cachedTicket.clientPhoneNumber : (existingTicket?.clientPhoneNumber ?? "")),
        shelfCodes: apiHasShelfCodes ? ticket.shelfCodes : (cachedTicket?.shelfCodes && cachedTicket.shelfCodes.trim() !== "" ? cachedTicket.shelfCodes : (existingTicket?.shelfCodes ?? "")),
        shelfId: apiHasShelfCodes ? ticket.shelfCodes : (cachedTicket?.shelfCodes && cachedTicket.shelfCodes.trim() !== "" ? cachedTicket.shelfCodes : (existingTicket?.shelfCodes ?? existingTicket?.shelfId ?? "")),
        isPhoneNumber: ticket.isPhoneNumber ?? cachedTicket?.isPhoneNumber ?? existingTicket?.isPhoneNumber ?? false,
        terminalServiceId: ticket.terminalServiceId ?? existingTicket?.terminalServiceId,
        terminalService: ticket.terminalService ?? existingTicket?.terminalService,
        status: this.getStatusString(ticket.status),
        windowNumber: 0,
        dateCreated: ticket.dateCreated ?? existingTicket?.dateCreated,
        prefix: ticket.prefix ?? existingTicket?.prefix ?? ''
      };
      
      // Ensure isFromPhoneNumber is preserved from cache if needed
      if (ticketItem.terminalService?.service?.isFromPhoneNumber === undefined && cachedTicket?.isFromPhoneNumber !== undefined) {
        ticketItem.terminalService = {
          ...ticketItem.terminalService,
          service: {
            ...ticketItem.terminalService?.service,
            isFromPhoneNumber: cachedTicket.isFromPhoneNumber
          }
        };
      }
      newWaiting = [...newWaiting, ticketItem];
    }

    newCalled.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    newWaiting.sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));

    this.currentQueueState = {
      called: newCalled,
      waiting: newWaiting,
      countCalled: newCalled.length,
      countWaiting: newWaiting.length
    };

    this.queueViewListeners.forEach(l => { try { l(this.currentQueueState); } catch(e) {} });
  }

  getStatusString(statusEnum) {
    const statusMap = {
      1: 'Waiting',
      2: 'InProgress',
      3: 'Completed',
      4: 'Canceled'
    };
    return statusMap[statusEnum] || 'Unknown';
  }

  onQueueView(callback) {
    this.queueViewListeners.push(callback);
    return () => {
      this.queueViewListeners = this.queueViewListeners.filter(l => l !== callback);
    };
  }

  notifyConnectionStateListeners(state) {
    this.connectionStateListeners.forEach(l => { try { l(state); } catch(e) {} });
  }

  clearCache() {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  }

  async refreshQueue() {
    try {
      const stableCache = this.getStableCache();
      const data = await queueApi.getQueueView({ page: 1, pageSize: 50 });

      const mergeList = (items) => {
        if (!Array.isArray(items)) return [];
        return items.map((item) => this.mergeStableFieldsIntoTicket(item, stableCache.tickets?.[item?.id]));
      };

      const called = mergeList(data?.called);
      const waiting = mergeList(data?.waiting);

      this.currentQueueState = {
        called,
        waiting,
        countCalled: data?.countCalled || called.length,
        countWaiting: data?.countWaiting || waiting.length
      };

      const all = [...called, ...waiting];
      all.forEach((t) => {
        if (t?.id !== undefined && t?.id !== null) {
          const existingEntry = stableCache.tickets?.[t.id];
          const hasValidClientTypeId = t.clientTypeId && t.clientTypeId > 0;
          const hasValidClientTypeName = t.clientTypeName && t.clientTypeName.trim() !== "";
          const hasValidShelfCodes = t.shelfCodes && t.shelfCodes.trim() !== "";
          const hasValidClientPhoneNumber = t.clientPhoneNumber && t.clientPhoneNumber.trim() !== "";
          
          // Preserve existing cache entries, don't recreate from scratch
          if (existingEntry) {
            // Update only if API has meaningful data, otherwise preserve existing
            existingEntry.clientTypeId = hasValidClientTypeId ? t.clientTypeId : (existingEntry.clientTypeId && existingEntry.clientTypeId > 0 ? existingEntry.clientTypeId : 0);
            existingEntry.clientTypeName = hasValidClientTypeName ? t.clientTypeName : (existingEntry.clientTypeName && existingEntry.clientTypeName.trim() !== "" ? existingEntry.clientTypeName : "");
            existingEntry.shelfCodes = hasValidShelfCodes ? t.shelfCodes : (existingEntry.shelfCodes && existingEntry.shelfCodes.trim() !== "" ? existingEntry.shelfCodes : "");
            existingEntry.clientPhoneNumber = hasValidClientPhoneNumber ? t.clientPhoneNumber : (existingEntry.clientPhoneNumber && existingEntry.clientPhoneNumber.trim() !== "" ? existingEntry.clientPhoneNumber : "");
            existingEntry.isFromPhoneNumber = t.terminalService?.service?.isFromPhoneNumber ?? existingEntry.isFromPhoneNumber ?? false;
            existingEntry.isPhoneNumber = t.isPhoneNumber ?? existingEntry.isPhoneNumber ?? false;
          } else {
            // Create new cache entry only if it doesn't exist
            stableCache.tickets[t.id] = {
              id: t.id,
              clientTypeId: hasValidClientTypeId ? t.clientTypeId : 0,
              clientTypeName: hasValidClientTypeName ? t.clientTypeName : "",
              shelfCodes: hasValidShelfCodes ? t.shelfCodes : "",
              clientPhoneNumber: hasValidClientPhoneNumber ? t.clientPhoneNumber : "",
              isFromPhoneNumber: t.terminalService?.service?.isFromPhoneNumber ?? false,
              isPhoneNumber: t.isPhoneNumber ?? false
            };
          }
        }
      });
      
      this.saveStableCache(stableCache);

      this.queueViewListeners.forEach(l => { try { l(this.currentQueueState); } catch(e) {} });
      return true;
    } catch (e) {
      console.error("❌ [API] Хатои гирифтани маълумот:", e);
      return false;
    }
  }

  setInitialQueueState(queueData) {
    if (queueData) {
      this.currentQueueState = {
        called: queueData.called || [],
        waiting: queueData.waiting || [],
        countCalled: queueData.countCalled || queueData.called?.length || 0,
        countWaiting: queueData.countWaiting || queueData.waiting?.length || 0
      };
    }
  }

  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (err) {
        console.error("❌ [SignalR] Хатои қатъи пайваст:", err);
      }
    }
  }
}

const signalRInstance = new QueueSignalRService();
export default signalRInstance;